'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { m } from 'framer-motion';
import { ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { STATS } from '@/config/hero';
import { StatItem } from '@/components/hero/StatItem';
import { FlipText } from '@/components/hero/FlipText';

import { EditableContent } from './EditableContent';

const GlobalMap = dynamic(() => import('./GlobalMap'), {
  ssr: false,
  loading: () => <div className="w-full h-full min-h-[300px] bg-primary/5 rounded-full animate-pulse" />
});

const Hero = () => {
  const { t, isRTL } = useLanguage();
  const [mountGlobe, setMountGlobe] = React.useState(false);

  React.useEffect(() => {
    const idleCallback = (typeof window !== 'undefined' && 'requestIdleCallback' in window)
      ? (window as any).requestIdleCallback
      : (cb: any) => setTimeout(cb, 2000);

    const handle = idleCallback(() => {
      setMountGlobe(true);
    });

    return () => {
      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(handle);
      } else {
        clearTimeout(handle);
      }
    };
  }, []);

  return (
    <section
      dir={isRTL ? 'rtl' : 'ltr'}
      className={cn(
        "relative min-h-screen flex items-center justify-center bg-background overflow-hidden",
        isRTL && "rtl"
      )}
    >
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--muted)/0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--muted)/0.3)_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Animated gradient orbs - optimized for mobile performance (removed blur/mix-blend) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <m.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 -left-32 w-64 sm:w-96 h-64 sm:h-96 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/20 via-primary/5 to-transparent rounded-full will-change-transform"
        />

        {/* White/Silver Shine Effect */}
        <m.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[600px] h-[400px] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/20 dark:from-white/10 via-white/5 to-transparent rounded-full will-change-transform"
        />

        <m.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 -right-32 w-64 sm:w-96 h-64 sm:h-96 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/20 via-primary/5 to-transparent rounded-full will-change-transform"
        />
      </div>

      {/* Top Accent Line - Enhanced */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-50" />

      {/* Radial Gradient Overlay - Lighter */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)/0.03)_0%,transparent_70%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="flex flex-col-reverse lg:flex-row items-center justify-between gap-8 lg:gap-12 min-h-[80vh]">

          {/* Content Section */}
          <div className={cn(
            "flex-1 text-center lg:text-start space-y-6 max-w-2xl"
          )}>

            {/* Badge with gradient underline */}
            <div>
              <span className={cn(
                "relative inline-flex items-center gap-2 px-4 py-2 rounded-full",
                "bg-primary/10 border border-primary/20",
                "text-sm font-medium text-primary",
                "backdrop-blur-sm"
              )}>
                <Sparkles className="w-4 h-4" />
                <EditableContent id="hero.badge" showEditIcon={true} />
                <span className="absolute inset-x-0 w-2/3 mx-auto -bottom-px bg-gradient-to-r from-transparent via-primary/60 to-transparent h-px" />
              </span>
            </div>

            {/* Heading with Flip Text */}
            <div className="relative">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.2]">
                {isRTL ? (
                  <>
                    <span className="relative inline-block">
                      <EditableContent id="hero.banking" />
                      <span className="absolute inset-x-0 w-full -bottom-2 bg-gradient-to-r from-primary/40 via-primary/20 to-transparent h-px" />
                    </span>
                    <br />
                    <span className="text-muted-foreground text-2xl sm:text-3xl md:text-4xl">
                      <EditableContent id="hero.with" />
                    </span>{' '}
                    <FlipText isRTL={isRTL} />
                  </>
                ) : (
                  <>
                    <span className="relative inline-block">
                      <EditableContent id="hero.banking" />
                      <span className="absolute inset-x-0 w-full -bottom-1 bg-gradient-to-r from-transparent via-primary/40 via-primary/20 to-transparent h-px" />
                    </span>
                    <br />
                    <span className="text-muted-foreground text-2xl sm:text-3xl md:text-4xl">
                      <EditableContent id="hero.with" />
                    </span>{' '}
                    <FlipText isRTL={isRTL} />
                  </>
                )}
              </h1>
            </div>

            {/* Description */}
            <p className={cn(
              "text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0"
            )}>
              <EditableContent id="hero.description.channels" as="span" showEditIcon={true} />
            </p>

            {/* Trust badges */}
            <div className={cn(
              "flex items-center gap-4 flex-wrap justify-center lg:justify-start"
            )}>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-green-500" />
                <EditableContent id="hero.secure" showEditIcon={true} />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Zap className="w-4 h-4 text-yellow-500" />
                <EditableContent id="hero.fast" showEditIcon={true} />
              </div>
            </div>

            {/* CTA Buttons */}
            <div className={cn(
              "flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            )}>
              <Button
                variant="glow"
                size="lg"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="h-12 px-8 text-base rounded-xl group relative overflow-visible"
              >
                <EditableContent id="hero.cta.contact" showEditIcon={true} />
                <ArrowRight className={cn(
                  "w-5 h-5 transition-transform group-hover:translate-x-1 ms-2",
                  isRTL && "rotate-180 group-hover:-translate-x-1"
                )} />
                <span className="absolute inset-x-0 w-1/2 mx-auto -bottom-px bg-gradient-to-r from-transparent via-primary/80 to-transparent h-px" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('discover')?.scrollIntoView({ behavior: 'smooth' })}
                className="h-12 px-8 text-base rounded-xl border-border/50 hover:border-primary/50 hover:bg-primary/5 relative overflow-visible"
              >
                <EditableContent id="hero.cta.discover" showEditIcon={true} />
                <span className="absolute inset-x-0 w-1/2 mx-auto -bottom-px bg-gradient-to-r from-transparent via-primary/50 to-transparent h-px" />
              </Button>
            </div>

            {/* Stats Row - Inline */}
            <div className={cn(
              "relative flex flex-wrap items-center gap-4 sm:gap-6 pt-6",
              "p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/30",
              "justify-center lg:justify-start"
            )}>
              {/* Top gradient line */}
              <span className="absolute inset-x-0 w-1/3 mx-auto -top-px bg-gradient-to-r from-transparent via-primary/40 to-transparent h-px" />

              {STATS.map((stat, index) => (
                <React.Fragment key={index}>
                  <StatItem stat={stat} index={index} isRTL={isRTL} />
                  {index < STATS.length - 1 && (
                    <div className="hidden sm:block w-px h-6 bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
                  )}
                </React.Fragment>
              ))}

              {/* Bottom gradient line */}
              <span className="absolute inset-x-0 w-2/3 mx-auto -bottom-px bg-gradient-to-r from-transparent via-primary/30 to-transparent h-px" />
            </div>
          </div>

          {/* Globe Section */}
          <div className="flex-1 relative w-full min-h-[300px] h-[300px] sm:h-[350px] lg:h-[600px]">
            <div className="absolute inset-0 bg-primary/5 blur-3xl rounded-full opacity-30" />
            {mountGlobe ? (
              <GlobalMap className="w-full h-full" />
            ) : (
              <div className="w-full h-full min-h-[300px] bg-primary/5 rounded-full animate-pulse" />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
